import { useMemo } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import { useFilteredRows } from '../hooks/useFilteredRows';
import { statusKey, statusCategory, statusSortIdx, MANDATORY_STATUS, type StatusCategory } from '../lib/status';
import type { Issue } from '../types/issue';
import { fmtInt } from '../lib/format';
import { useT, format } from '../i18n';

// Statt CSS-Klassen: M3-konforme Tonale Farben pro Workflow-Phase.
// Inspiriert von der V1-Logik, aber an MUI's Palette ausgerichtet.
const CATEGORY_COLOR: Record<StatusCategory, string> = {
  new:        '#9e9e9e',
  open:       '#90a4ae',
  refine:     '#7e57c2',
  backlog:    '#5e7186',
  ready:      '#5677a3',
  progress:   '#1976d2',
  review:     '#7b1fa2',
  integ:      '#00838f',
  'await-test':'#ef6c00',
  test:       '#f57c00',
  resolved:   '#388e3c',
  closed:     '#2e7d32',
  done:       '#43a047',
  aborted:    '#616161',
  rejected:   '#c62828',
};

export function StatusBreakdown() {
  const rows = useFilteredRows();
  const theme = useTheme();
  const t = useT();

  const data = useMemo(() => {
    const counts: Record<string, number> = {};
    let total = 0;
    for (const r of rows as Issue[]) {
      const t = r['Issue Type'];
      if (t !== 'Story' && t !== 'Bug') continue;
      const s = (r['Status'] || '').trim();
      if (!s) continue;
      counts[s] = (counts[s] || 0) + 1;
      total++;
    }
    const seen = new Set<string>();
    const all: string[] = [];
    for (const s of MANDATORY_STATUS) { all.push(s); seen.add(statusKey(s)); }
    for (const s of Object.keys(counts)) {
      if (!seen.has(statusKey(s))) { all.push(s); seen.add(statusKey(s)); }
    }
    all.sort((a, b) => statusSortIdx(a) - statusSortIdx(b));
    return {
      total,
      entries: all.map(s => ({
        status: s,
        count: counts[s] || 0,
        cat: statusCategory(s),
      })),
    };
  }, [rows]);

  if (data.total === 0 && rows.length > 0) {
    return null; // Nur Epics geladen, keine Stories/Bugs → kein Status zeigen
  }
  if (rows.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="overline" color="text.secondary">{t.statusBreakdown.title}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {format(t.statusBreakdown.subtitle, { count: fmtInt(data.total) })}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {data.entries.map(e => {
            const pct = data.total > 0 ? Math.round((e.count / data.total) * 100) : 0;
            const dim = e.count === 0;
            return (
              <Tooltip
                key={e.status}
                title={`${e.status}: ${fmtInt(e.count)} (${pct} %)`}
                arrow
              >
                <Box
                  sx={{
                    minWidth: 92,
                    px: 1.5, py: 1,
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                    opacity: dim ? 0.45 : 1,
                    cursor: 'help',
                  }}
                >
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: CATEGORY_COLOR[e.cat] }} />
                    <Typography variant="caption" noWrap title={e.status}>
                      {e.status}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography variant="h6" sx={{ lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                      {fmtInt(e.count)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{pct}%</Typography>
                  </Stack>
                </Box>
              </Tooltip>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}
